import { Button, UnassignIcon, media } from '@higo/ui';
import { FormattedMessage } from 'react-intl';
import { MouseEvent, useCallback } from 'react';
import { useUnassignDoctorVisitMutation } from '@higo/doctor/src/pages/Dashboard/DashboardVisits/useUnassignDoctorVisitMutation';
import styled from 'styled-components';
import { ExaminationQueryKey } from '@higo/common/lib/features/ExaminationPage/ExaminationQueryKey';
import { useQueryClient } from 'react-query';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { useVisitsStoreActions } from 'store/visitsStore';

export type UnassignFromVisitPageButtonProps = {
  visitId: number;
  isSideSheetPanel?: boolean;
  onUnassigned?: () => void | Promise<void>;
};

export const UnassignFromVisitPageButton = ({
  visitId,
  isSideSheetPanel,
  onUnassigned,
}: UnassignFromVisitPageButtonProps) => {
  const queryClient = useQueryClient();
  const { examAssignmentPermission } = useUserPermissions();
  const isUnassignExamPermission = examAssignmentPermission?.delete;
  const { diagnosisPermission } = useUserPermissions();
  const isDiagnosticCreatePermission = diagnosisPermission?.create;

  const StyledButtonWrapper = styled('div')`
    display: inline;
    align-self: end;
    margin-bottom: 0.4rem;
    margin-left: auto;

    ${media.mobile.max} {
      margin: 0 1.3rem 0.4rem auto;
    }
  `;

  const StyledButton = styled(Button)`
    height: 1.5rem;
    width: 14.5rem;
    padding: 1rem;
    margin-bottom: 3rem;
  `;

  const StyledButtonForSideSheet = styled(Button)`
    height: 1.5rem;
    width: 9rem;
    padding: 1rem;
  `;

  const { mutateAsync: unassignVisit } = useUnassignDoctorVisitMutation();

  const { addNewVisit } = useVisitsStoreActions();

  const handleClick = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();

      await unassignVisit(visitId);

      const id = Number(visitId);
      if (Number.isFinite(id) && id > 0) {
        addNewVisit(id);
      }

      await queryClient.invalidateQueries([
        ExaminationQueryKey.ExaminationDetails,
        visitId,
      ]);
      await onUnassigned?.();
    },
    [visitId, unassignVisit, addNewVisit, queryClient, onUnassigned],
  );

  if (!(isUnassignExamPermission && isDiagnosticCreatePermission)) return null;

  const Btn = isSideSheetPanel ? StyledButtonForSideSheet : StyledButton;

  return (
    <StyledButtonWrapper>
      <Btn
        size="small"
        color="secondary"
        endIcon={<UnassignIcon />}
        onClick={handleClick}
        id="unassign-from-exam-button"
      >
        <FormattedMessage id="common.unassign" />
      </Btn>
    </StyledButtonWrapper>
  );
};
